// Implements: F-IDN-012, F-IDN-016, F-UIX-010
/**
 * P2 `/recuperar`: ask for a recovery link (`POST /auth/password-reset/request`, sent once).
 *
 * **One answer for every address.** The server answers `202` whether or not the address has an
 * account, and this page says the same «Revisa tu correo … si tiene una cuenta» for every `202`:
 * nothing on screen tells a registered address from an unknown one (v3 note «sent»).
 *
 * What it refuses itself: an address that does not look like one (the v3 pattern), under the field,
 * with no request. What the server refuses: a body its schema rejects (`422`, said at the field the
 * same way), the login's shared bucket (`429`: the wait, the button held) and anything else (the
 * v3 «No se pudo enviar el correo ahora» above the field, the address kept).
 *
 * The focus goes to the `h1` when the page opens and every time the card changes (form ↔ sent).
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { Link } from "react-router";

import { asApiError } from "../../../shared/api/asApiError";
import type { ApiError } from "../../../shared/api/ApiError";
import { LOGIN_ROUTE } from "../../../shared/api/nextParam";
import { useRateLimitWait } from "../../../shared/api/useRateLimitWait";
import { useTranslation } from "../../../shared/i18n";
import { Button, buttonClassName } from "../../../shared/ui/atoms/Button";
import { RateLimitWait } from "../../../shared/ui/molecules/RateLimitWait";
import { ForgotPasswordForm } from "../components/ForgotPasswordForm";
import { RecoveryOutcome } from "../components/RecoveryOutcome";
import { useRequestPasswordReset } from "../hooks/usePasswordRecovery";

/** The v3 `forgot-form` pattern: something, an at sign, a domain with a dot. */
const ADDRESS = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;

/** A marker the sentence is split on, so the address can be set in bold inside it. */
const SLOT = "\u0000";

export function ForgotPasswordContainer(): ReactElement {
  const { t } = useTranslation();
  const request = useRequestPasswordReset();
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [limit, setLimit] = useState<ApiError | null>(null);
  const waitSeconds = useRateLimitWait(limit);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);

  const view = sentTo === null ? "form" : "sent";
  useEffect(() => {
    headingRef.current?.focus();
  }, [view]);

  if (sentTo !== null) {
    const [before = "", after = ""] = t("auth.recovery.sent.body", { email: SLOT }).split(SLOT);
    return (
      <RecoveryOutcome
        icon="mail"
        title={t("auth.recovery.sent.title")}
        headingRef={headingRef}
        body={
          <>
            {before}
            <b>{sentTo}</b>
            {after}
          </>
        }
        actions={
          <>
            <Link to={LOGIN_ROUTE} className={buttonClassName("primary")}>
              {t("auth.recovery.back")}
            </Link>
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                setEmail("");
                setSentTo(null);
              }}
            >
              {t("auth.recovery.again")}
            </Button>
          </>
        }
      />
    );
  }

  const submit = (): void => {
    const address = email.trim();
    setFormError(undefined);
    if (!ADDRESS.test(address)) {
      setFieldError(t("auth.recovery.invalidEmail"));
      emailRef.current?.focus();
      return;
    }
    setFieldError(undefined);
    request.mutate(address, {
      onSuccess: () => {
        setLimit(null);
        setSentTo(address);
      },
      onError: (cause) => {
        const error = asApiError(cause);
        if (error.status === 429) {
          setLimit(error);
        } else if (error.status === 422) {
          setFieldError(t("auth.recovery.invalidEmail"));
          emailRef.current?.focus();
        } else {
          setFormError(t("auth.recovery.failed"));
        }
      },
    });
  };

  const waiting = waitSeconds !== null;
  return (
    <ForgotPasswordForm
      email={email}
      onEmailChange={(value) => {
        setEmail(value);
        setFieldError(undefined);
      }}
      onSubmit={submit}
      submitting={request.isPending}
      disabled={waiting}
      fieldError={fieldError}
      formError={formError}
      notice={waiting ? <RateLimitWait seconds={waitSeconds} /> : null}
      loginPath={LOGIN_ROUTE}
      headingRef={headingRef}
      emailRef={emailRef}
    />
  );
}
